import { createContext, FC, ReactNode, useContext, useEffect, useState } from "react";
import { UserManager, User, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { WebUser } from "models/WebUser";
import { HttpRequest } from "libs/http-helper";

export enum UserPropertyName {
    DISPLAY_NAME = "DisplayName",
    LANGUAGE_CODE = "LanguageCode",
    WEBID = "WebId",
    LOCALE_ID = "LocaleId",
    COUNTRY_CODE = "CountryCode"
};

const OICD_PREFIX = "http://oidc.apci.com";

export enum OidcClaimName {
    LANGUAGE_CODE = "http://oidc.apci.com/lang_code",
    WEBID = "http://oidc.apci.com/web_id",
    COUNTRY_CODE = "http://oidc.apci.com/country_code",
    LOCALE_ID = "http://oidc.apci.com/locale_id"
};

export type AuthContextState = {
    authenticating: boolean;
    user: User | null;
    logIn: () => void;
    finishLogin: () => Promise<User>;
    logOut: () => void;
    finishLogout: () => Promise<void>;
    isAuthenticated: () => boolean;
    getUserProperty: (propertyName: UserPropertyName) => string;
    getUserLanguage: () => string;
    getToken: () => string;
};

const AuthContextDefaultState: AuthContextState = {
    authenticating: true,
    user: null,
    logIn: () => { },
    finishLogin: () => Promise.resolve(null as any),
    logOut: () => { },
    finishLogout: () => Promise.resolve(),
    isAuthenticated: () => false,
    getUserProperty: (propertyName: UserPropertyName) => "",
    getUserLanguage: () => "",
    getToken: () => ""
};

export const AuthContext = createContext(AuthContextDefaultState);

const defaultUserManagerConfig: UserManagerSettings = {
    authority: `${import.meta.env.VITE_APIM_BASE_URL}/oauth`,
    client_id: import.meta.env.VITE_CLIENT_ID!,
    redirect_uri: `${import.meta.env.VITE_MYAP_ORDER_SITE_URL}/signin-oidc/`,
    metadata: {
        issuer: `${import.meta.env.VITE_APIM_BASE_URL}/oauth`,
        authorization_endpoint: `${import.meta.env.VITE_APIM_BASE_URL}/oauth/authorize`,
        token_endpoint: `${import.meta.env.VITE_APIM_BASE_URL}/oauth/token`,
        jwks_uri: `${import.meta.env.VITE_APIM_BASE_URL}/oauth/keys`,
        end_session_endpoint: `${import.meta.env.VITE_MYAP_ORDER_SITE_URL}/signout-oidc`,
    },
    monitorSession: false,
    scope: "openid profile",
    response_type: "code",
    post_logout_redirect_uri: `${import.meta.env.VITE_MYAP_ORDER_SITE_URL}`,
    filterProtocolClaims: true,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.localStorage })
};

interface AuthProviderProps {
    children: ReactNode;
}

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [authenticating, setAuthenticating] = useState(true);
    const [backendUser, setBackendUser] = useState<WebUser | null>(null);
    const [userManager] = useState(new UserManager(defaultUserManagerConfig));

    useEffect(() => {
        async function getUserFromBackend() {
            const usr = await HttpRequest<WebUser>("get", `${import.meta.env.VITE_ONLINE_SHOP_AUTH_API_URL}/user`, null, true);
            console.log('backend user retrieved from webapi is:');
            console.log(usr);
            return usr;
        };
        async function EvaluateAuth() {
            setAuthenticating(true);
            var [_user, _backendUser] = await Promise.all([userManager.getUser(), getUserFromBackend()]);
            if (_backendUser && _backendUser.auth && !_backendUser.exp) {
                _user && setUser(_user);
                setBackendUser(_backendUser);
            } else {
                _user && await userManager.removeUser();
            }
            setAuthenticating(false);
        };
        EvaluateAuth();
    }, []);

    const isAuthenticated = () => !!((user && !user.expired) || (backendUser && backendUser.auth && !backendUser.exp));

    const getUserLanguage = () => {
        return isAuthenticated() ? getUserProperty(UserPropertyName.LANGUAGE_CODE) : "";
    };

    const getUserProperty = (propertyName: UserPropertyName): string => {
        return (user && user.profile) ? getOauthUserProperty(propertyName) : getFormAuthUserProperty(propertyName);
    };

    const getToken = () => backendUser?.token as string;

    const getOauthUserProperty = (propertyName: UserPropertyName): string => {
        switch (propertyName) {
            case UserPropertyName.DISPLAY_NAME: return `${user?.profile.given_name} ${user?.profile.family_name}`;
            case UserPropertyName.LANGUAGE_CODE: return (user?.profile[OidcClaimName.LANGUAGE_CODE] as string) ?? "";
            case UserPropertyName.WEBID: return (user?.profile[OidcClaimName.WEBID] as string) ?? "";
            case UserPropertyName.LOCALE_ID: return (user?.profile[OidcClaimName.LOCALE_ID] as string) ?? "";
            case UserPropertyName.COUNTRY_CODE: return (user?.profile[OidcClaimName.COUNTRY_CODE] as string) ?? "";
            default: return "";
        }
    };

    const getFormAuthUserProperty = (propertyName: UserPropertyName): string => {
        switch (propertyName) {
            case UserPropertyName.DISPLAY_NAME: return `${backendUser?.firstName} ${backendUser?.lastName}`;
            case UserPropertyName.LANGUAGE_CODE: return (backendUser?.langCode as string) ?? "";
            case UserPropertyName.WEBID: return backendUser?.webId?.toString() ?? "";
            case UserPropertyName.COUNTRY_CODE: return (backendUser?.countryCode as string) ?? "";
            case UserPropertyName.LOCALE_ID: return backendUser?.localeId?.toString() ?? "";
            default: return "";
        }
    };

    const logIn = () => {
        return userManager.signinRedirect();
    };
    const finishLogin = () => {
        return userManager.signinRedirectCallback()
            .then(_user => {
                setUser(_user);
                return _user;
            });
    };
    const logOut = () => {
        userManager.signoutRedirect();
    };
    const finishLogout = () => {
        return userManager.signoutRedirectCallback()
            .then(response => {
                setUser(null);
                console.log('Current user has been signed out!');
            });
    };
    return <AuthContext.Provider value={{ user, authenticating, logIn, finishLogin, logOut, finishLogout, isAuthenticated, getUserLanguage, getUserProperty, getToken }}>
        {children}
    </AuthContext.Provider>
};

export default AuthProvider;
export const useAuth = () => useContext(AuthContext);